import { cphqApiUrl } from '@/endpoints/endpoints';
import { NetworkPartnership, Whitelabel } from '@/utils/whitelabels';

export type CompanyBranding = {
  companyCode: string;
  whiteLabel: Partial<Whitelabel> | null;
  networkPartnership: NetworkPartnership | null;
};

type CompanyBrandingResponse = {
  responseContent?: {
    companyCode?: string;
    whiteLabel?: Partial<Whitelabel> | null;
    network?: {
      text?: string;
      logoUrl?: string;
    } | null;
  };
};

function mapNetworkPartnership(
  network?: { text?: string; logoUrl?: string } | null,
): NetworkPartnership | null {
  if (network?.text && network?.logoUrl) {
    return { text: network.text, logoUrl: network.logoUrl };
  }
  return null;
}

/**
 * Resolves company branding from the API.
 * whiteLabel overrides are not yet returned by the API (phase 2+).
 */
export async function resolveCompanyBranding(
  subdomain: string,
  whitelabel: Whitelabel,
): Promise<CompanyBranding> {
  const fallback: CompanyBranding = {
    companyCode: whitelabel.code,
    whiteLabel: null,
    networkPartnership: null,
  };

  if (!subdomain) {
    return fallback;
  }

  try {
    const response = await fetch(
      `${cphqApiUrl}v1/WhiteLabels/Branding?subdomain=${encodeURIComponent(subdomain)}`,
    );

    if (!response.ok) {
      return fallback;
    }

    const data = (await response.json()) as CompanyBrandingResponse;
    const content = data.responseContent;

    return {
      companyCode: content?.companyCode ?? whitelabel.code,
      whiteLabel: content?.whiteLabel ?? null,
      networkPartnership: mapNetworkPartnership(content?.network),
    };
  } catch {
    return fallback;
  }
}

/** @deprecated Use resolveCompanyBranding — kept for callers that only need partnership. */
export async function resolveLoginPartnership(
  subdomain: string,
  whitelabel: Whitelabel,
): Promise<NetworkPartnership | null> {
  const branding = await resolveCompanyBranding(subdomain, whitelabel);
  return branding.networkPartnership;
}
